import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../core/services/auth.service';
import { environment } from '../../environments/environment';

/**
 * JWT/Bearer-Variante von nonce.interceptor.ts (siehe ADR 0003). Beide
 * Interceptoren existieren parallel — welcher aktiv ist, entscheidet die
 * Build-Zeit-Strategie in auth-strategy.providers(.jwt).ts, kein Laufzeit-Branching.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router      = inject(Router);

  const apiUrl = environment.apiUrl;
  if (!apiUrl || !req.url.startsWith(apiUrl)) {
    return next(req);
  }

  const isLoginRequest   = req.url === `${apiUrl}auth/login`;
  const isRefreshRequest = req.url === `${apiUrl}auth/refresh`;

  const token = authService.accessTokenValue();
  const authReq = req.clone({
    withCredentials: true,
    headers: (!isLoginRequest && token)
      ? req.headers.set('Authorization', `Bearer ${token}`)
      : req.headers,
  });

  return next(authReq).pipe(
    catchError((error: unknown) => {
      const is401 = error instanceof HttpErrorResponse && error.status === 401;

      // Ein 401 auf /auth/refresh selbst darf NIE einen weiteren Refresh
      // auslösen (sonst Endlos-Loop) — sofort Logout + Redirect.
      if (!is401 || isRefreshRequest || isLoginRequest) {
        return throwError(() => error);
      }

      return authService.refresh().pipe(
        switchMap(() => {
          const retryReq = req.clone({
            withCredentials: true,
            headers: req.headers.set('Authorization', `Bearer ${authService.accessTokenValue()}`),
          });
          // next() direkt aufrufen, NICHT erneut durch diesen Interceptor
          // schicken – verhindert Rekursion bei einem zweiten 401.
          return next(retryReq);
        }),
        catchError(refreshError => {
          authService.logout().subscribe();
          router.navigate(['/login']);
          return throwError(() => refreshError);
        })
      );
    })
  );
};
